import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@neo/primitives/collapsible'
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@neo/primitives/sidebar'
import { Tooltip } from '@neo/primitives/tooltip'
import { ChevronDown, Lock } from 'lucide-react'
import { Link, useLocation } from 'router-v6'
import {
  activeItemKey,
  isLinkActive,
  type ResolvedNavGroup,
  type ResolvedNavLink,
} from 'v2/shared/navigation/workspace-nav-utils'

export const useCloseMobileSidebar = () => {
  const { isMobile, setOpenMobile } = useSidebar()
  return () => {
    if (isMobile) setOpenMobile(false)
  }
}

// A ghosted item that opens the upgrade request instead of navigating; `sub` for a group's page.
const LockedItem = ({ link, sub }: { link: ResolvedNavLink; sub?: boolean }) => {
  const closeMobile = useCloseMobileSidebar()
  const Item = sub ? SidebarMenuSubItem : SidebarMenuItem
  const Button = sub ? SidebarMenuSubButton : SidebarMenuButton
  return (
    <Item>
      <Tooltip content={link.lockedTooltip} delayDuration={300}>
        <Button asChild className={sub ? 'h-8 opacity-50' : 'opacity-50'}>
          <button
            type="button"
            onClick={() => {
              closeMobile()
              link.onLockedClick?.()
            }}
            data-testid={`sidebar-locked-item-${link.key}`}
          >
            {!sub && link.icon && <link.icon className="size-3.5" />}
            <span className="flex-1 text-left">{link.title}</span>
            <Lock className="ml-auto size-3 shrink-0" strokeWidth={2.5} />
          </button>
        </Button>
      </Tooltip>
    </Item>
  )
}

export const NavLinkItem = ({ link, exact }: { link: ResolvedNavLink; exact?: boolean }) => {
  const { pathname } = useLocation()
  const closeMobile = useCloseMobileSidebar()
  if (link.locked) return <LockedItem link={link} />
  const isActive = exact ? pathname === link.path : isLinkActive(pathname, link)

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive} tooltip={link.title}>
        <Link
          to={link.path}
          aria-label={link.title}
          aria-current={isActive ? 'page' : undefined}
          onClick={closeMobile}
          data-testid={`link-nav-${link.key}`}
        >
          {link.icon && <link.icon className="size-3.5" />}
          <span>{link.title}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

const NavSubLink = ({ link, isActive }: { link: ResolvedNavLink; isActive: boolean }) => {
  const closeMobile = useCloseMobileSidebar()

  if (link.locked) return <LockedItem link={link} sub />

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild isActive={isActive} className="h-8">
        <Link
          to={link.path}
          aria-current={isActive ? 'page' : undefined}
          onClick={closeMobile}
          data-testid={`link-inline-${link.key}`}
        >
          <span>{link.title}</span>
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  )
}

interface NavGroupItemProps {
  group: ResolvedNavGroup
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
}

export const NavGroupItem = ({ group, expanded, onExpandedChange }: NavGroupItemProps) => {
  const { open, setOpen, isMobile } = useSidebar()
  const activeKey = activeItemKey(useLocation().pathname, group.items)

  return (
    <Collapsible
      asChild
      open={expanded}
      onOpenChange={(next) => {
        // On the icon rail a group has nowhere to open, so widen the sidebar first.
        if (!open && !isMobile) {
          setOpen(true)
          onExpandedChange(true)
          return
        }
        onExpandedChange(next)
      }}
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={group.title}
            aria-label={group.title}
            data-testid={`button-expand-${group.key}`}
          >
            <group.icon className="size-3.5" />
            <span className="flex-1">{group.title}</span>
            <ChevronDown
              className={`size-4 transition-transform group-data-[collapsible=icon]:hidden motion-reduce:transition-none ${expanded ? '' : '-rotate-90'}`}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub aria-label={`${group.title} pages`}>
            {group.items.map((item) => (
              <NavSubLink key={item.key} link={item} isActive={item.key === activeKey} />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}
